import { type ReactNode, useState } from 'react';

import Warning from '@/assets/svg/warning.svg?react';
import InfoPopover from '@/components/InfoPopover';
import { SpinnerOverlay } from '@/components/loaders';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Link, Text } from '@/components/typography';
import { cn } from '@/lib/utils';

import DeltaLabel from './DeltaLabel';
import KpiMetricLoader from './KpiMetricLoader';

export type KpiMetricProps = {
  ['data-testid']?: string | null;
  className?: string;
  color?: string;
  label: ReactNode;
  infoPopover?: {
    title?: ReactNode; // use this if you want a different title for this than `title`
    body: ReactNode;
  };
  value?: number | string | null;
  valueLink?: string | false | 0;
  percentChange?: number | null | false;
  caption?: ReactNode;
  captionLink?: string | false;
  isLoading?: boolean;
  error: Error | undefined | null;
};

const KpiMetric = ({
  ['data-testid']: dataTestId = null,
  className = '',
  color = undefined,
  label,
  infoPopover,
  value,
  valueLink,
  percentChange,
  caption,
  captionLink,
  isLoading,
  error,
}: KpiMetricProps) => {
  const [stickyValue, setStickyValue] = useState<number | string | null>(value ?? null);

  if (value != null && value !== stickyValue) {
    setStickyValue(value);
  }

  const showSkeletonLoader = isLoading && stickyValue == null;
  const showSpinnerLoader = isLoading && stickyValue != null;

  return (
    <SpinnerOverlay className="flex h-full min-h-0 flex-col" isSpinning={!!showSpinnerLoader}>
      <div
        className={cn(
          'relative flex flex-1 flex-col rounded-2xl border-2 border-gray-300 bg-white px-4 py-3 shadow-lg/5',
          className,
        )}
        style={{ backgroundColor: color, borderColor: color }}
        data-testid={dataTestId}
      >
        <Text className="mb-1.5 inline-block" variant="footnote" color="dark-grey" weight="regular">
          <span className="flex items-start gap-1">
            {label}
            {infoPopover && (
              <InfoPopover title={infoPopover.title ?? label} body={infoPopover.body} />
            )}
          </span>
        </Text>
        {!!error && <Warning className="h-7.5 w-6" />}
        {!error && showSkeletonLoader && <KpiMetricLoader />}
        {!error && !showSkeletonLoader && (
          <>
            <div className="flex flex-1 flex-wrap items-start gap-x-2">
              <Tooltip disabled={stickyValue == null}>
                <TooltipTrigger
                  delay={1750}
                  render={
                    valueLink ? (
                      <Link
                        to={valueLink}
                        className="overflow-hidden text-ellipsis"
                        variant="kpi"
                        weight="medium"
                      >
                        {stickyValue}
                      </Link>
                    ) : (
                      <Text className="overflow-hidden text-ellipsis" variant="kpi" weight="medium">
                        {stickyValue}
                      </Text>
                    )
                  }
                />
                <TooltipContent>{stickyValue}</TooltipContent>
              </Tooltip>
              {percentChange !== false && percentChange !== undefined && (
                <DeltaLabel className="h-8" percentChange={percentChange} />
              )}
            </div>
            {
              <Text className="relative -bottom-1 leading-3" variant="caption1" weight="light">
                {!!captionLink && (
                  <Link to={captionLink} variant="caption1" color="static-black">
                    {caption || '\u00A0'}
                  </Link>
                )}
                {!captionLink && (caption || '\u00A0')}
              </Text>
            }
          </>
        )}
      </div>
    </SpinnerOverlay>
  );
};

export default KpiMetric;
